import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileSpreadsheet, Trash2, DoorClosed, ArrowRight, Loader2, Search, Copy, X, GitCompare, FileText, Archive, RotateCcw, FolderArchive } from "lucide-react";
import { PriceAlert } from "@/components/PriceAlert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";

const fmt = (n) => `S/ ${Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [emptyOpen, setEmptyOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  const loadTrash = () => api.get("/quotes/trash").then((r) => setTrash(r.data)).catch((e) => toast.error(apiErr(e)));
  const openTrash = () => { setTrashOpen(true); setTrash(null); loadTrash(); };

  const restoreTrash = async (id) => {
    try { await api.post(`/quotes/${id}/restore-trash`); toast.success("Cotización restaurada"); loadTrash(); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const purgePermanent = async () => {
    if (!purgeTarget) return;
    try { await api.delete(`/quotes/${purgeTarget.id}/permanent`); toast.success("Eliminada definitivamente"); setPurgeTarget(null); loadTrash(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const emptyTrash = async () => {
    try { await api.delete(`/quotes/trash/empty`); toast.success("Papelera vaciada"); setEmptyOpen(false); loadTrash(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const daysLeft = (expires_at) => {
    if (!expires_at) return null;
    const ms = new Date(expires_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 2 ? [s[1], id] : [...s, id]));
  };
  const compareItems = () => (quotes || []).filter((q) => selected.includes(q.id));

  const exportComparison = async () => {
    const ids = selected.join(",");
    try {
      const res = await api.get(`/quotes/compare/export/pdf?ids=${ids}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "comparacion.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo exportar la comparación"); }
  };

  const load = () => api.get("/quotes").then((r) => setQuotes(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!quotes) return [];
    const term = q.trim().toLowerCase();
    return quotes.filter((item) => {
      if (term) {
        const c = item.client || {};
        const hay = `${item.name || ""} ${c.proyecto || ""} ${c.cliente || ""} ${c.ciudad || ""} ${c.codigo || ""} ${c.contacto || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (item.updated_at) {
        const d = item.updated_at.slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      return true;
    });
  }, [quotes, q, from, to]);

  const archive = async (id, e) => {
    e.stopPropagation();
    try {
      await api.post(`/quotes/${id}/archive`);
      setSelected((s) => s.filter((x) => x !== id));
      toast.success("Cotización archivada", { action: { label: "Ver archivo", onClick: () => nav("/archivo") } });
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const duplicate = async (id, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/quotes/${id}/duplicate`);
      toast.success("Cotización duplicada");
      nav(`/quote/${data.id}`);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const createNew = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/quotes", { name: "Nueva cotización", client: {}, doors: [] });
      toast.success("Cotización creada");
      nav(`/quote/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setCreating(false); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    const quote = (quotes || []).find((x) => x.id === id);
    setDelTarget(quote || { id });
    setDelConfirm("");
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/quotes/${delTarget.id}`);
      toast.success("Movida a la papelera");
      setDelTarget(null);
      setDelConfirm("");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setDeleting(false); }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Cotizaciones</h1>
          <p className="text-slate-500 mt-1 text-sm">
            {isAdmin ? "Gestiona proyectos, costos y precios de venta." : "Crea y edita cotizaciones (verás solo los precios de venta)."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button data-testid="open-trash-btn" onClick={openTrash}
              className="flex items-center gap-2 border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold px-4 py-2.5 rounded-md transition-colors">
              <Archive className="h-4 w-4" /> Papelera
            </button>
          )}
          {selected.length === 2 && (
            <button data-testid="compare-btn" onClick={() => setCompareOpen(true)}
              className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2.5 rounded-md transition-colors">
              <GitCompare className="h-4 w-4" /> Comparar (2)
            </button>
          )}
          <button data-testid="new-quote-btn" onClick={createNew} disabled={creating}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2.5 rounded-md transition-colors disabled:opacity-60">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Nueva cotización
          </button>
        </div>
      </div>

      {quotes === null ? (
        <div className="text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</div>
      ) : quotes.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-16 text-center bg-white" data-testid="empty-state">
          <FileSpreadsheet className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No hay cotizaciones todavía.</p>
        </div>
      ) : (
        <>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, cliente o ciudad..."
              className="w-full pl-9 pr-8 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-500">Desde
            <input data-testid="filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="px-2 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white" /></label>
          <label className="flex items-center gap-1.5 text-xs text-slate-500">Hasta
            <input data-testid="filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="px-2 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm bg-white" /></label>
          {(q || from || to) && (
            <button data-testid="clear-filters" onClick={() => { setQ(""); setFrom(""); setTo(""); }}
              className="text-xs text-slate-500 hover:text-slate-900 underline">Limpiar</button>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center bg-white text-slate-500 text-sm">
            No se encontraron cotizaciones con esos filtros.
          </div>
        ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="quotes-grid">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-3 py-2.5 w-8"></th>
                <th className="px-4 py-2.5 text-left">Proyecto</th>
                <th className="px-4 py-2.5 text-left hidden sm:table-cell">Cliente</th>
                <th className="px-4 py-2.5 text-right w-20">Puertas</th>
                <th className="px-4 py-2.5 text-right w-40 hidden md:table-cell">Venta total</th>
                <th className="px-4 py-2.5 text-right w-28 hidden lg:table-cell">Actualizado</th>
                <th className="px-4 py-2.5 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr key={q.id} data-testid={`quote-card-${q.id}`} onClick={() => nav(`/quote/${q.id}`)}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer group">
                  <td className="px-3 py-3" onClick={(e) => toggleSelect(q.id, e)}>
                    <input type="checkbox" data-testid={`select-${q.id}`} checked={selected.includes(q.id)}
                      onChange={() => {}} className="h-4 w-4 accent-emerald-600 cursor-pointer" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                        <DoorClosed className="h-4 w-4 text-slate-700" />
                      </div>
                      <div className="min-w-0">
                        <span className="font-medium text-slate-900 truncate block" data-testid={`quote-project-${q.id}`}>
                          {q.client?.proyecto || q.name || "Sin proyecto"}
                        </span>
                        {q.client?.codigo ? (
                          <span className="text-xs font-mono text-slate-400 truncate block" data-testid={`quote-code-${q.id}`}>
                            {q.client.codigo}
                          </span>
                        ) : null}
                        <PriceAlert quote={q} isAdmin={isAdmin} onUpdated={load} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 hidden sm:table-cell truncate">
                    {q.client?.cliente || "—"}{q.client?.ciudad ? ` · ${q.client.ciudad}` : ""}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{q.total_doors}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-600 font-semibold hidden md:table-cell">{fmt(q.venta_total)}</td>
                  <td className="px-4 py-3 text-right text-xs text-slate-400 hidden lg:table-cell">
                    {q.updated_at ? q.updated_at.slice(0, 10) : ""}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button data-testid={`duplicate-quote-${q.id}`} onClick={(e) => duplicate(q.id, e)} title="Duplicar"
                        className="text-slate-300 hover:text-emerald-600 transition-colors">
                        <Copy className="h-4 w-4" />
                      </button>
                      {isAdmin && (
                        <button data-testid={`archive-quote-${q.id}`} onClick={(e) => archive(q.id, e)} title="Archivar"
                          className="text-slate-300 hover:text-slate-700 transition-colors">
                          <FolderArchive className="h-4 w-4" />
                        </button>
                      )}
                      {isAdmin && (
                        <button data-testid={`delete-quote-${q.id}`} onClick={(e) => del(q.id, e)} title="Eliminar"
                          className="text-slate-300 hover:text-red-500 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}

      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Comparar cotizaciones</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4" data-testid="compare-modal">
            {compareItems().map((q) => (
              <div key={q.id} className="border border-slate-200 rounded-lg p-4">
                <h4 className="font-display font-bold text-slate-900 truncate">{q.client?.proyecto || q.name}</h4>
                <p className="text-xs text-slate-500 mt-0.5 truncate mb-3">
                  {q.client?.codigo ? `${q.client.codigo} · ` : ""}{q.client?.cliente || "Sin cliente"}
                </p>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-500">Puertas</dt><dd className="font-mono font-semibold">{q.total_doors}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Venta total</dt><dd className="font-mono font-semibold text-emerald-600">{fmt(q.venta_total)}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Actualizado</dt><dd className="text-xs text-slate-400">{q.updated_at ? q.updated_at.slice(0, 10) : ""}</dd></div>
                </dl>
              </div>
            ))}
          </div>
          {compareItems().length === 2 && (
            <div className="mt-3 text-sm bg-slate-50 border border-slate-200 rounded-md px-4 py-3" data-testid="compare-diff">
              <div className="flex justify-between"><span className="text-slate-500">Diferencia de puertas</span>
                <span className="font-mono font-semibold">{compareItems()[1].total_doors - compareItems()[0].total_doors}</span></div>
              <div className="flex justify-between mt-1"><span className="text-slate-500">Diferencia de venta</span>
                <span className="font-mono font-semibold text-emerald-600">{fmt(compareItems()[1].venta_total - compareItems()[0].venta_total)}</span></div>
            </div>
          )}
          {compareItems().length === 2 && (
            <button data-testid="compare-export-pdf" onClick={exportComparison}
              className="mt-4 flex items-center justify-center gap-1.5 text-sm bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2 rounded-md transition-colors w-full">
              <FileText className="h-4 w-4 text-red-400" /> Exportar comparación (PDF)
            </button>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelConfirm(""); } }}>
        <DialogContent className="max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Eliminar cotización
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Estás a punto de eliminar permanentemente{" "}
              <span className="font-semibold text-slate-900">
                {delTarget?.client?.proyecto || delTarget?.name || "esta cotización"}
              </span>
              {delTarget?.client?.cliente ? <> del cliente <span className="font-semibold text-slate-900">{delTarget.client.cliente}</span></> : null}
              . Se moverá a la papelera y podrás restaurarla durante 30 días antes de que se borre de forma permanente.
            </p>
            <div>
              <label className="text-xs font-medium text-slate-500">
                Escribe <span className="font-mono font-bold text-red-600">ELIMINAR</span> para confirmar
              </label>
              <input data-testid="delete-confirm-input" value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)}
                autoFocus placeholder="ELIMINAR"
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="delete-cancel-btn" onClick={() => { setDelTarget(null); setDelConfirm(""); }}
                className="px-4 py-2 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
                Cancelar
              </button>
              <button data-testid="delete-confirm-btn" onClick={confirmDelete}
                disabled={delConfirm.trim().toUpperCase() !== "ELIMINAR" || deleting}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Mover a papelera
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent className="max-w-3xl" data-testid="trash-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Archive className="h-5 w-5" /> Papelera
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Las cotizaciones eliminadas se conservan 30 días y luego se borran automáticamente.</p>
          {trash && trash.length > 0 && (
            <div className="flex justify-end -mt-1">
              <button data-testid="empty-trash-btn" onClick={() => setEmptyOpen(true)}
                className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Vaciar papelera ({trash.length})
              </button>
            </div>
          )}
          {trash === null ? (
            <div className="text-slate-400 flex items-center gap-2 py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</div>
          ) : trash.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500 text-sm">
              La papelera está vacía.
            </div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2.5 text-left">Proyecto</th>
                    <th className="px-4 py-2.5 text-left hidden sm:table-cell">Cliente</th>
                    <th className="px-4 py-2.5 text-right w-28">Expira en</th>
                    <th className="px-4 py-2.5 w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {trash.map((t) => (
                    <tr key={t.id} data-testid={`trash-row-${t.id}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900 block truncate">{t.client?.proyecto || t.name || "Sin proyecto"}</span>
                        {t.deleted_by ? <span className="text-xs text-slate-400">Eliminada por {t.deleted_by}</span> : null}
                      </td>
                      <td className="px-4 py-3 text-slate-500 hidden sm:table-cell truncate">{t.client?.cliente || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-semibold text-amber-600">{daysLeft(t.expires_at)} día(s)</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3">
                          <button data-testid={`restore-trash-${t.id}`} onClick={() => restoreTrash(t.id)} title="Restaurar"
                            className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition-colors">
                            <RotateCcw className="h-3.5 w-3.5" /> Restaurar
                          </button>
                          <button data-testid={`purge-trash-${t.id}`} onClick={() => setPurgeTarget(t)} title="Eliminar definitivamente"
                            className="text-slate-300 hover:text-red-500 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!purgeTarget} onOpenChange={(o) => { if (!o) setPurgeTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Eliminar definitivamente
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Se eliminará de forma permanente{" "}
            <span className="font-semibold text-slate-900">{purgeTarget?.client?.proyecto || purgeTarget?.name || "esta cotización"}</span>{" "}
            y su historial de versiones. Esta acción <span className="font-semibold">no se puede deshacer</span>.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button data-testid="purge-cancel-btn" onClick={() => setPurgeTarget(null)}
              className="px-4 py-2 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">Cancelar</button>
            <button data-testid="purge-confirm-btn" onClick={purgePermanent}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors">
              <Trash2 className="h-4 w-4" /> Eliminar para siempre
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={emptyOpen} onOpenChange={setEmptyOpen}>
        <DialogContent className="max-w-md" data-testid="empty-trash-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" /> Vaciar papelera
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Se eliminarán de forma permanente <span className="font-semibold text-slate-900">todas</span> las cotizaciones de la papelera
            {trash ? <> (<span className="font-semibold">{trash.length}</span>)</> : null} junto con su historial de versiones. Esta acción <span className="font-semibold">no se puede deshacer</span>.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button data-testid="empty-cancel-btn" onClick={() => setEmptyOpen(false)}
              className="px-4 py-2 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">Cancelar</button>
            <button data-testid="empty-confirm-btn" onClick={emptyTrash}
              className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors">
              <Trash2 className="h-4 w-4" /> Vaciar todo
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
